import { useTranslation } from 'react-i18next';

import Warning from '@/assets/icons/warning.svg?component';
import WarningCircle from '@/assets/icons/warning_circle.svg?component';
import { Badge, type BadgeTone } from '@/components/atoms/Badge';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type { HealthOverviewParameter } from '@/domain/health';

import type {
    HealthAlertBadgeVariant,
    HealthAlertDisplayLevel,
} from '../../HealthDashboard/healthAlertDisplay';
import {
    getHealthParameterStatusDisplayLevel,
    getHealthParameterStatusLabel,
    getHealthParameterStatusLevel,
    getHealthParameterStatusTooltip,
} from '../../HealthDashboard/healthParameterMetricDisplay';
import HealthAlarmMetricTooltip from '../HealthAlarmMetricTooltip/HealthAlarmMetricTooltip';

import styles from './HealthParameterStatusMetric.module.scss';

export interface HealthParameterStatusMetricProps {
    parameter: HealthOverviewParameter;
    variant?: HealthAlertBadgeVariant;
}

// The alert card's pills: high is red, medium orange and low yellow, as the inline text.
const PILL_TONE: Record<HealthAlertDisplayLevel | 'normal', BadgeTone> = {
    high: 'danger',
    medium: 'elevated',
    low: 'warning',
    normal: 'neutral',
};

export default function HealthParameterStatusMetric(props: HealthParameterStatusMetricProps) {
    const { parameter, variant = 'inline' } = props;
    const { t } = useTranslation('healthModule');

    const status = getHealthParameterStatusLevel(parameter);
    const displayLevel = getHealthParameterStatusDisplayLevel(parameter);
    const label = getHealthParameterStatusLabel(status, t);
    const tooltip = getHealthParameterStatusTooltip(status, t);

    const icon =
        status === 'normal' ? null : (
            <SvgIcon
                icon={displayLevel === 'low' ? WarningCircle : Warning}
                size="custom"
                customSize={16}
            />
        );

    const badge =
        variant === 'pill' ? (
            <Badge size="md" tone={PILL_TONE[displayLevel ?? 'normal']} icon={icon}>
                {label}
            </Badge>
        ) : (
            <span
                className={styles.status}
                data-display-level={displayLevel ?? 'normal'}
                data-testid={`health-status-metric-${status}`}>
                {icon && <span className={styles.icon}>{icon}</span>}
                <span className={styles.label}>{label}</span>
            </span>
        );

    if (tooltip == null) {
        return badge;
    }

    return (
        <HealthAlarmMetricTooltip label={label} description={tooltip} variant={variant}>
            {badge}
        </HealthAlarmMetricTooltip>
    );
}
